import { Capacitor } from '@capacitor/core';
import { LocalNotifications } from '@/lib/notifications/local-notifications';

// ============================================================================
// Notification LOCALE mobile « ton défi commence dans 1 min ». Device-local
// (Capacitor Local Notifications) — pas de trace serveur : c'est l'email
// (10 min avant, cron) qui est le rappel fiable multi-appareils ; la notif
// locale est le bonus natif de dernière minute pour l'élève inscrit.
//
// La PLANIFICATION est pure (testable) ; les appels Capacitor sont un mince
// wrapper natif-only (no-op sur le web). Le plugin est atteint par
// `registerPlugin` (cf. `lib/notifications/local-notifications.ts`) et JAMAIS
// par un import dynamique : celui-ci peut rester bloqué dans le WKWebView iOS.
// ============================================================================

const isNative = Capacitor.isNativePlatform();

/** Défi minimal nécessaire pour planifier sa notif (sous-ensemble de LiveEvent). */
export interface NotifiableChallenge {
  id: string;
  starts_at: string;
  title: string | null;
}

export interface PlannedNotif {
  id: number;
  challengeId: string;
  at: number; // epoch ms de déclenchement (starts_at - lead)
  title: string;
  body: string;
}

export interface LiveNotifPlan {
  toSchedule: PlannedNotif[];
  toCancel: number[];
}

/** Avance du rappel local : 1 minute avant le coup d'envoi. */
export const LIVE_NOTIF_LEAD_MS = 60_000;

/**
 * Id de notification déterministe (32-bit positif) dérivé de l'uuid du défi —
 * l'API Capacitor exige un entier. Hash FNV-1a. PUR.
 */
export function challengeNotifId(challengeId: string): number {
  let h = 0x811c9dc5;
  for (let i = 0; i < challengeId.length; i++) {
    h ^= challengeId.charCodeAt(i);
    h = Math.imul(h, 0x01000193);
  }
  // 31 bits positifs (évite les ids négatifs / 0).
  return ((h >>> 0) % 2147483646) + 1;
}

/**
 * Diff PUR entre l'état voulu (inscriptions) et les défis connus : programme
 * une notif 1 min avant pour chaque défi INSCRIT encore à venir, annule les
 * autres (désinscrit, ou trop tard). Déterministe — `now` passé explicitement.
 */
export function planLiveNotifications(
  challenges: NotifiableChallenge[],
  registeredIds: ReadonlySet<string>,
  now: number,
  leadMs: number = LIVE_NOTIF_LEAD_MS,
): LiveNotifPlan {
  const toSchedule: PlannedNotif[] = [];
  const toCancel: number[] = [];
  for (const c of challenges) {
    const id = challengeNotifId(c.id);
    const at = new Date(c.starts_at).getTime() - leadMs;
    if (registeredIds.has(c.id) && at > now) {
      toSchedule.push({
        id,
        challengeId: c.id,
        at,
        title: 'Ton défi Live commence dans 1 min ! ⚡',
        body: `${c.title ?? 'Défi Live School'} — prépare-toi, ça démarre.`,
      });
    } else {
      // Pas (plus) inscrit, ou trop tard → on nettoie une éventuelle notif posée.
      toCancel.push(id);
    }
  }
  return { toSchedule, toCancel };
}

/**
 * Réconcilie les notifications locales avec l'état d'inscription courant.
 * No-op hors natif. Best-effort : toute erreur est avalée (l'email reste le
 * rappel fiable). Demande la permission uniquement s'il y a quelque chose à
 * programmer (déclenché par un geste utilisateur — clic « Je participe »).
 */
export async function syncLiveNotifications(
  challenges: NotifiableChallenge[],
  registeredIds: ReadonlySet<string>,
): Promise<void> {
  await applyPlan(planLiveNotifications(challenges, registeredIds, Date.now()), '[live-notif]');
}

/**
 * Pose / retire les notifications d'un plan. No-op hors natif. Best-effort :
 * toute erreur est avalée (le rappel par email reste le filet fiable). La
 * permission n'est demandée que s'il y a quelque chose à programmer.
 */
async function applyPlan(plan: LiveNotifPlan, tag: string): Promise<void> {
  if (!isNative) return;
  if (plan.toSchedule.length === 0 && plan.toCancel.length === 0) return;
  try {
    if (plan.toSchedule.length > 0) {
      const perm = await LocalNotifications.checkPermissions();
      if (perm.display !== 'granted') {
        const req = await LocalNotifications.requestPermissions();
        if (req.display !== 'granted') return;
      }
    }
    if (plan.toCancel.length > 0) {
      await LocalNotifications.cancel({ notifications: plan.toCancel.map((id) => ({ id })) });
    }
    if (plan.toSchedule.length > 0) {
      await LocalNotifications.schedule({
        notifications: plan.toSchedule.map((n) => ({
          id: n.id,
          title: n.title,
          body: n.body,
          schedule: { at: new Date(n.at), allowWhileIdle: true },
          extra: { challengeId: n.challengeId },
        })),
      });
    }
  } catch (err) {
    console.warn(`${tag} sync échec`, err);
  }
}

// ============================================================================
// Défis PRIVÉS programmés (`get_my_private_lives`) — même mécanique.
// ============================================================================

/** Défi privé minimal nécessaire pour programmer son rappel. */
export interface NotifiablePrivateLive {
  id: string;
  title: string | null;
  /** null quand l'organisateur démarre à la main : pas d'heure, pas de rappel. */
  starts_at: string | null;
  /** L'organisateur n'a pas encore programmé d'heure. */
  awaiting_host: boolean;
  status: string;
}

/**
 * Diff PUR : un rappel 1 min avant chaque défi privé qui a une HEURE et n'a pas
 * encore commencé ; on annule les autres (démarrage à la main, déjà joué,
 * annulé, trop tard). `now` passé explicitement — déterministe.
 */
export function planPrivateLiveNotifications(
  lives: NotifiablePrivateLive[],
  now: number,
  leadMs: number = LIVE_NOTIF_LEAD_MS,
): LiveNotifPlan {
  const toSchedule: PlannedNotif[] = [];
  const toCancel: number[] = [];
  for (const live of lives) {
    const id = challengeNotifId(live.id);
    const startsAt = live.starts_at ? new Date(live.starts_at).getTime() : NaN;
    const at = startsAt - leadMs;
    const scheduled =
      !live.awaiting_host &&
      Number.isFinite(at) &&
      at > now &&
      (live.status === 'scheduled' || live.status === 'live');
    if (scheduled) {
      toSchedule.push({
        id,
        challengeId: live.id,
        at,
        title: 'Ton défi commence dans 1 min ! ⚡',
        body: `${live.title ?? 'Défi entre amis'} — prépare-toi, ça démarre.`,
      });
    } else {
      toCancel.push(id);
    }
  }
  return { toSchedule, toCancel };
}

/** Réconcilie les rappels des défis privés. No-op hors natif, ne lève jamais. */
export async function syncPrivateLiveNotifications(lives: NotifiablePrivateLive[]): Promise<void> {
  await applyPlan(planPrivateLiveNotifications(lives, Date.now()), '[private-live-notif]');
}
